// Friend-request banner — direct mirror of game-invitation-banner.
// Subscribes to the shared site SSE stream for friend-request pokes,
// reads from notifications-state, and renders a centered modal with
// Accept / Reject for the most recent unseen incoming request.

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { notificationsState } from '../../state/notifications-state.js';
import { presenceState } from '../../state/presence-state.js';

const SEEN_STORAGE_KEY = 'rpgish-seen-friend-requests';

class FriendRequestBanner extends BaseComponent {
  static properties = {
    _dismissed: { type: Boolean, state: true },
    _busy: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      padding: 1rem;
    }

    .modal-panel {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      width: 100%;
      max-width: 380px;
      overflow: hidden;
      text-align: center;
    }

    .modal-body {
      padding: 1.75rem 1.5rem 1.5rem;
    }

    .icon {
      color: #28a745;
      margin-bottom: 0.75rem;
    }

    .heading {
      font-size: 1.15rem;
      font-weight: 600;
      color: #212529;
      margin: 0 0 0.25rem;
    }

    .from {
      font-size: 1rem;
      color: #495057;
      margin: 0 0 1.25rem;
    }

    .actions {
      display: flex;
      gap: 0.5rem;
    }

    .btn {
      flex: 1;
      padding: 0.7rem;
      font-size: 0.95rem;
      font-weight: 600;
      border: none;
      border-radius: 8px;
      cursor: pointer;
    }

    .btn.accept {
      background: #28a745;
      color: white;
    }

    .btn.reject {
      background: #f1f3f5;
      color: #495057;
    }

    .modal-footer {
      padding: 0 1.5rem 1.25rem;
      display: flex;
      justify-content: center;
    }

    .dismiss {
      font-size: 0.85rem;
      color: #6c757d;
      background: none;
      border: none;
      padding: 0.25rem;
      cursor: pointer;
    }
  `;

  constructor() {
    super();
    this._dismissed = false;
    this._busy = false;
    /** @type {Set<string>} */
    this._seenIds = new Set(JSON.parse(localStorage.getItem(SEEN_STORAGE_KEY) || '[]'));
    /** @type {(() => void) | null} */
    this._unsubscribe = null;
    this._lastSeenCount = 0;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._unsubscribe = notificationsState.subscribe(() => {
      // A new incoming request undismisses the banner.
      const incoming = this._unseenIncoming();
      if (incoming.length > this._lastSeenCount) {
        this._dismissed = false;
      }
      this._lastSeenCount = incoming.length;
      this.requestUpdate();
    });
    notificationsState.refetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    this._unsubscribe = null;
  }

  _unseenIncoming() {
    return notificationsState.notifications.filter(
      (n) =>
        n.kind === 'friend_request' &&
        n.friendRequest?.direction === 'incoming' &&
        n.friendRequest?.status === 'pending' &&
        !this._seenIds.has(n.friendRequest.id),
    );
  }

  _markSeen(/** @type {string} */ requestId) {
    this._seenIds.add(requestId);
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...this._seenIds]));
    } catch {
      // localStorage full — ignore.
    }
  }

  _dismiss(/** @type {string} */ requestId) {
    this._markSeen(requestId);
    this._dismissed = true;
  }

  async _accept(/** @type {string} */ requestId) {
    if (this._busy) return;
    this._busy = true;
    try {
      await api.acceptFriendRequest(requestId);
      this._markSeen(requestId);
      this._dismissed = true;
      await notificationsState.refetch();
      presenceState.refetch();
    } catch (err) {
      console.warn('[friend-request-banner] accept failed', err);
    } finally {
      this._busy = false;
    }
  }

  async _reject(/** @type {string} */ requestId) {
    if (this._busy) return;
    this._busy = true;
    try {
      await api.rejectFriendRequest(requestId);
      this._markSeen(requestId);
      this._dismissed = true;
      await notificationsState.refetch();
    } catch (err) {
      console.warn('[friend-request-banner] reject failed', err);
    } finally {
      this._busy = false;
    }
  }

  render() {
    const incoming = this._unseenIncoming();
    if (!incoming.length || this._dismissed) return '';

    const latest = incoming[0];
    const fr = latest.friendRequest;

    return html`
      <div class="modal-backdrop" @click="${() => this._dismiss(fr.id)}">
        <div class="modal-panel" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
          <div class="modal-body">
            <div class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" x2="19" y1="8" y2="14" />
                <line x1="22" x2="16" y1="11" y2="11" />
              </svg>
            </div>
            <p class="heading">${fr.otherUsername} wants to be your friend</p>
            <p class="from">Accept the request to add them to your friends list.</p>
            <div class="actions">
              <button class="btn reject" ?disabled="${this._busy}" @click="${() => this._reject(fr.id)}">Decline</button>
              <button class="btn accept" ?disabled="${this._busy}" @click="${() => this._accept(fr.id)}">Accept</button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="dismiss" @click="${() => this._dismiss(fr.id)}">Not now</button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('friend-request-banner', FriendRequestBanner);
